import { zodResolver } from '@hookform/resolvers/zod'
import { Stack, Typography } from '@sureco/design-system'
import { Button, Modal } from '@surecompanies/core_components'
import RHFSelect from 'enrollment-platform/components/form/hookform/Select/RHFSelect'
import React from 'react'
import { useForm } from 'react-hook-form'
import RowSection from '../RowSection'
import {
  isMedicareAmountSign,
  MedicareAmountSign,
  MedicareColumnsFormValues,
  MedicareColumnsSchema,
} from './medicareColumns.schema'

type MedicareSignMode = 'hidden' | 'editable' | 'readOnly'

const MEDICARE_SIGN_LABELS: Record<MedicareAmountSign, string> = {
  POSITIVE: 'Positive',
  NEGATIVE: 'Negative',
  NONE: 'N/A',
}

const MEDICARE_SIGN_OPTIONS = Object.entries(MEDICARE_SIGN_LABELS).map(
  ([value, label]) => ({ value, label })
)

const getMedicareSignMode = (
  isMedicarePerColumnEnabled: boolean,
  hasStoredMedicarePair: boolean
): MedicareSignMode => {
  if (isMedicarePerColumnEnabled) return 'editable'
  return hasStoredMedicarePair ? 'readOnly' : 'hidden'
}

// Shows a hand-written stored token as-is rather than hiding it behind a dash.
const formatMedicareSign = (value: unknown) => {
  if (isMedicareAmountSign(value)) return MEDICARE_SIGN_LABELS[value]
  return typeof value === 'string' && value ? value : '—'
}

type MedicareColumnsFormProps = {
  medicareEmployeeSign?: string
  medicareEmployerSign?: string
  isMedicarePerColumnEnabled: boolean
  isSaving: boolean
  onFormSubmit: (data: MedicareColumnsFormValues) => Promise<unknown>
}

export default function MedicareColumnsForm({
  medicareEmployeeSign,
  medicareEmployerSign,
  isMedicarePerColumnEnabled,
  isSaving,
  onFormSubmit,
}: MedicareColumnsFormProps) {
  const medicareSignMode = getMedicareSignMode(
    isMedicarePerColumnEnabled,
    Boolean(medicareEmployeeSign || medicareEmployerSign)
  )

  const { control, handleSubmit, trigger } = useForm<MedicareColumnsFormValues>(
    {
      // A hand-written stored token starts empty, so saving forces a valid choice.
      defaultValues: {
        medicareEmployeeSign: isMedicareAmountSign(medicareEmployeeSign)
          ? medicareEmployeeSign
          : undefined,
        medicareEmployerSign: isMedicareAmountSign(medicareEmployerSign)
          ? medicareEmployerSign
          : undefined,
      },
      resolver: zodResolver(MedicareColumnsSchema),
    }
  )

  if (medicareSignMode === 'hidden') return null

  const handleSaveClick = async () => {
    const isValid = await trigger()

    if (!isValid) {
      return
    }

    Modal.confirm({
      title: 'Save payroll configuration format?',
      content:
        'The Medicare EE and ER columns will use these signs from the next payroll push.',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: async () => {
        await handleSubmit(async (data) => {
          await onFormSubmit(data)
        })()
      },
    })
  }

  return (
    <RowSection title="Payroll Configuration Format">
      {medicareSignMode === 'editable' ? (
        <form>
          <Stack space={12}>
            <Stack direction="row" space={20}>
              <RHFSelect
                name="medicareEmployeeSign"
                control={control}
                label="Medicare EE column"
                aria-label="medicareEmployeeSign"
                options={MEDICARE_SIGN_OPTIONS}
                style={{ width: 200 }}
              />
              <RHFSelect
                name="medicareEmployerSign"
                control={control}
                label="Medicare ER column"
                aria-label="medicareEmployerSign"
                options={MEDICARE_SIGN_OPTIONS}
                style={{ width: 200 }}
              />
            </Stack>
            <Stack direction="row" justify="flex-start">
              <Button
                type="primary"
                onClick={handleSaveClick}
                loading={isSaving}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </form>
      ) : (
        <Stack direction="row" space={20}>
          <Typography size={14}>
            Medicare EE column: {formatMedicareSign(medicareEmployeeSign)}
          </Typography>
          <Typography size={14}>
            Medicare ER column: {formatMedicareSign(medicareEmployerSign)}
          </Typography>
        </Stack>
      )}
    </RowSection>
  )
}
